// "N people need hire details" — My Desk / Home, OWNER and DIRECTOR (N-084,
// Request Ledger, owner 2026-09-08; Candace added 2026-10-06, "yes to all"
// on the view-differences audit B list).
//
// db/load_week_package.js CREATEs a person straight off a Labor Distribution
// Register with file #, name and role and nothing else — no hire date, no
// contact. That is invisible on Home unless something says so; this is that
// line, following RaiseAlerts.tsx's own pattern (a useResource-backed alert,
// reading a snapshot primed at sign-in so ordinary navigation costs nothing —
// INV-1).
//
// Server-gated OWNER/DIRECTOR (src/people.js, GET /api/v1/people/needs-details)
// — this client gate now matches it exactly (canSeeNewHires below), and is
// the one place People.tsx's badge, bootstrap's priming and the desk catalog
// all ask.
import type { JSX } from 'preact';
import { session } from '../lib/session';
import { useResource } from '../lib/store';
import { authedGet } from '../lib/apiClient';
import { navigate } from '../lib/router';
import { DeskPanel } from './DeskPanel';

export const NEEDS_HIRE_DETAILS_KEY = 'people-needs-details:v1';

export interface NeedsHireDetailsPerson {
  id: number;
  full_name: string;
  file_no: string | null;
  role: string | null;
  home_location_code: string;
  location_name: string;
}

export interface NeedsHireDetailsData {
  people: NeedsHireDetailsPerson[];
}

/** Owner and director — the same two roles the server route allows. */
export function canSeeNewHires(role: string | null | undefined): boolean {
  return role === 'OWNER' || role === 'DIRECTOR';
}

function fetchNeedsHireDetails(): Promise<NeedsHireDetailsData> {
  return authedGet<NeedsHireDetailsData>('/api/v1/people/needs-details');
}

export function NeedsHireDetailsAlert(
  { chromeless = false }: { chromeless?: boolean } = {},
): JSX.Element | null {
  const allowed = canSeeNewHires(session.value.me?.role);

  // Hooks run every render regardless of the early return below (rules of
  // hooks), same discipline as RaiseAlerts.
  const load = useResource<NeedsHireDetailsData>(
    allowed ? NEEDS_HIRE_DETAILS_KEY : null,
    fetchNeedsHireDetails,
  );

  if (!allowed) return null;
  const people = load.status === 'ready' ? load.data.people : [];
  if (!people.length) {
    // Same N-090c reasoning as RaiseAlerts.tsx: only name the empty state on
    // the canvas (chromeless) — this file's own dedicated tests render it
    // standalone and expect nothing at all when the list is empty.
    if (!chromeless) return null;
    return (
      <DeskPanel title="New Hires" testId="needs-hire-details-panel" chromeless>
        <p class="empty">No one is missing hire details.</p>
      </DeskPanel>
    );
  }

  return (
    <DeskPanel title="New Hires" testId="needs-hire-details-panel" chromeless={chromeless}>
      <div class="queue">
        <div class="q needs-hire-details-alert" data-testid="needs-hire-details-alert">
          <div class="code">People</div>
          <div>
            <b>
              {people.length} {people.length === 1 ? 'person needs' : 'people need'} hire details
            </b>
            <span>
              {people.slice(0, 3).map((p) => p.full_name).join(', ')}
              {people.length > 3 ? `, +${people.length - 3} more` : ''}
              {' — created from a register import with no hire date on file'}
            </span>
          </div>
          <button
            type="button"
            class="btn"
            onClick={() => navigate('/people')}
          >
            Open People
          </button>
        </div>
      </div>
    </DeskPanel>
  );
}
